import React, { useCallback, useEffect, useId, useState } from 'react';
import { fetchJSON } from '../../utils/api';
import { recordNotice, btnOutline } from '../workflow/constants';
import { FormModal, btnPrimary, btnGhost } from '../shared/FormKit';

// ── Legacy import（上傳簽署表單）的共用零件 ──────────────────────────────────
//
// 系統外簽好的 QC6/QC7／QC35 進系統時仍是一筆普通記錄，只多兩件事：一份上傳的
// 簽署 PDF、以及「只填了下游會用到的欄位」。這裡收的是各畫面都要用到的零件：
//
//   useLegacyImportSetting   Management 的兩顆開關（GET /api/qm/legacy-import）——
//                            enabled：關閉時表單不提供「Upload signed form」模式
//                            allowMissingSignedForm：開著時匯入記錄可先不附簽署 PDF
//   （開關本體在 pages/SettingsLegacyImport.js——Management → Admin Settings → Legacy Import）
//   ImportModeSwitch         表單步驟上的兩種填法切換
//   LegacyImportBadge        清單／標題旁的「Imported」徽章
//   SignedFormPendingNotice  記錄頁頂端的一句話——簽署檔還沒上傳
//   FormPdfDownload          系統產生 Form PDF 的下載鈕——匯入記錄先跳提示
//
// 「待補」不是欄位：後端由「沒有現行簽署檔」推導出 `signedFormPending`，第一次
// 從 Management 編輯補傳就自然清掉。**記錄是上傳建立的**不另外公告——標題的
// Imported 徽章與 Record 卡的 Signed form 格已經說了；只有缺件才值得一句話。

export const useLegacyImportSetting = () => {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [allowMissingSignedForm, setAllowMissingSignedForm] = useState(false);
  const [error, setError] = useState(null);
  const apply = (data) => {
    setEnabled(!!data.enabled);
    setAllowMissingSignedForm(!!data.allowMissingSignedForm);
  };
  const load = useCallback(async () => {
    try {
      apply(await fetchJSON('/api/qm/legacy-import'));
      setError(null);
    } catch (e) { setError(e.message); setEnabled(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  /** `update(true)` flips the main switch; `update({ allowMissingSignedForm })` the other. */
  const update = useCallback(async (next) => {
    const body = typeof next === 'boolean' ? { enabled: next } : next;
    const data = await fetchJSON('/api/qm/legacy-import', { method: 'PUT', body: JSON.stringify(body) });
    apply(data);
    return !!data.enabled;
  }, []);
  return {
    enabled, allowMissingSignedForm, loading: enabled === null, error, reload: load, update,
    // Both on: a record may be created before its signed PDF is at hand.
    missingSignedFormAllowed: enabled === true && allowMissingSignedForm,
  };
};

/**
 * Two ways to fill the form step — only offered while the switch is on.
 * radio 的 name 每個實例各一（useId）：多家分頁同時掛在頁上，同名會變成同一組，
 * 勾一家就把別家的勾掉。
 */
export const ImportModeSwitch = ({ value, onChange, disabled = false, formName = 'QC6 / QC7 form' }) => {
  const name = `qcImportMode-${useId()}`;
  return (
    <div className="flex flex-wrap gap-4 text-sm text-slate-700">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" name={name} checked={!value} disabled={disabled} onChange={() => onChange(false)} />
        Fill in the {formName}
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="radio" name={name} checked={!!value} disabled={disabled} onChange={() => onChange(true)} />
        Upload signed form
        <span className="text-xs text-slate-500">— attach the signed PDF, answer only the fields the system acts on</span>
      </label>
    </div>
  );
};

export const LegacyImportBadge = ({ className = '' }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ${className}`}
    title="Imported from a signed form uploaded in Management — only the answers the system acts on are filled in"
  >
    Imported
  </span>
);

/**
 * 現行的簽署檔（最新一版）。有 `superseded`／`supersededAt` 的信封先濾掉舊版；
 * Engagement 頁的檔案清單沒有這兩欄，取 id 最大的那份。
 */
const currentSignedFile = (record, signedDocType) => {
  const signed = (record.files || []).filter((f) => f.docType === signedDocType && !f.superseded && !f.supersededAt);
  return signed.reduce((latest, f) => (!latest || f.id > latest.id ? f : latest), null);
};

/**
 * 系統產生的 Form PDF 下載鈕。一般記錄直接下載；匯入記錄（`legacyImport`）的
 * 表單只填了系統會用到的欄位，先跳提示：
 *   有簽署檔 ⇒ 二選一：簽署檔（主按鈕）或系統版本
 *   待補     ⇒ 說明內容不完整，確認後才下載
 * 「待補」以信封的 `signedFormPending` 為準；沒有這欄的（Engagement 頁）看檔案清單。
 *   href           系統版本的下載網址
 *   signedHref(f)  簽署檔的下載網址
 *   signedDocType  簽署檔的 doc_type（QC6/QC7＝QC_FORM、QC35＝qc35）
 */
export const FormPdfDownload = ({
  record, href, signedHref, signedDocType = 'QC_FORM', formName, className, title, children,
}) => {
  const [open, setOpen] = useState(false);
  if (!record || record.legacyImport !== true) {
    return <a href={href} target="_blank" rel="noreferrer" className={className} title={title}>{children}</a>;
  }
  const signed = record.signedFormPending === true ? null : currentSignedFile(record, signedDocType);
  const close = () => setOpen(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} title={title}>{children}</button>
      {open && (
        <FormModal
          title="Imported Record"
          size="md"
          onClose={close}
          footer={(
            <>
              <button type="button" onClick={close} className={btnGhost}>Cancel</button>
              {signed ? (
                <>
                  <a href={href} target="_blank" rel="noreferrer" onClick={close} className={`${btnOutline} whitespace-nowrap`}>
                    Download System Version
                  </a>
                  <a href={signedHref(signed)} target="_blank" rel="noreferrer" onClick={close} className={`${btnPrimary} whitespace-nowrap`}>
                    Download Signed Form
                  </a>
                </>
              ) : (
                <a href={href} target="_blank" rel="noreferrer" onClick={close} className={`${btnPrimary} whitespace-nowrap`}>
                  Download Anyway
                </a>
              )}
            </>
          )}
        >
          <p className="text-sm text-slate-700">
            {signed
              ? `This record was imported from a signed form. The system version of Form ${formName} only contains the fields the system uses; the rest is blank.`
              : `This record was imported and the signed form has not been uploaded yet. The downloaded Form ${formName} only contains the fields the system uses; the rest is blank.`}
          </p>
        </FormModal>
      )}
    </>
  );
};

/**
 * 記錄頁頂端的一句話，QC6/QC7 與 QC35 共用：簽署檔還沒上傳。補齊了就整條不
 * 印——沒有缺的東西就沒有要說的話。句子刻意短：怎麼補件由 Edit 裡那格自己說
 * （「Upload the signed form」），不在這裡重複一次操作指引。
 */
export const SignedFormPendingNotice = ({ pending = false }) => (
  pending ? (
    <div className={recordNotice}>The signed form has not been uploaded yet.</div>
  ) : null
);
